/**
 * Design-QA route for the SkillTree (mirrors AdminReview, ClusterNReview).
 * Mounts <SkillTree> with the live course content + empty completions so
 * every lesson appears in its progression-locked default state. Useful for
 * iterating on character placement, serpentine spacing, unit tints, etc.
 * without needing a real Supabase session or any progress.
 */
import { Container, Screen } from '@fluent/ui';
import { SkillTree, type NextMotion } from './home/SkillTree.js';
import { getCourseTree } from './content/course.js';
import { buildProgressionView, type LessonCompletion } from './progress/progression.js';

export function SkillTreeReview() {
  const tree = getCourseTree();
  if (!tree) return <div>No course tree.</div>;
  // `?done=N` marks the first N lessons complete, so the next-lesson node
  // (and how it moves) can be checked at any point of the path; `?motion=`
  // picks the motion (hop | pulse | wiggle) to compare the options.
  const params = new URLSearchParams(window.location.search);
  const done = Number(params.get('done') ?? 0) || 0;
  const m = params.get('motion');
  const motion: NextMotion | undefined = m === 'hop' || m === 'pulse' || m === 'wiggle' ? m : undefined;
  const completions: Record<string, LessonCompletion> = {};
  tree.units
    .flatMap((u) => u.resolvedSkills.flatMap((s) => s.resolvedLessons))
    .slice(0, done)
    .forEach((l) => {
      completions[l.id] = { lessonId: l.id, xp: 10, accuracy: 1, perfect: false, completedAt: 0 };
    });
  const view = buildProgressionView(tree, completions);
  return (
    <Screen>
      <Container size="sm">
        <SkillTree view={view} nextMotion={motion} />
      </Container>
    </Screen>
  );
}
